<script setup lang="ts">
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";
import { DollarSign, ArrowUp, ArrowDown, ChevronRight } from "lucide-vue-next";
import { useStoreApp } from "~/stores/storeApp";
import { formatNumberLocal, roundToDecimals } from "~/utils/math";

import { useStoreUserV2 } from "~/stores/storeUserV2";
import { useWeb3AuthStore } from "~/stores/storeWeb3auth";

const { t } = useI18n();

const userStore = useStoreUserV2();
const web3AuthStore = useWeb3AuthStore();
const { username, email } = storeToRefs(userStore);

const storeApp = useStoreApp();

const showDepositModal = () => {
  userStore.closeProfileDrawer();
  storeApp.showDepositModal = true;
};

const showTransferModal = () => {
  const usersToBlock = [
    "cmbjiwge000020fs629jh9rrs", // Hiks79
    "cmdn4olgp00010gs6c66mdp00", // SpiderCam
    "cm876zjq300020fs6gjbjdox3", // beyondbig44
    "cmcuj2qll00100gs6duy9d4tx", // JackSock
  ];

  if (usersToBlock.includes(userStore.cuid)) return;
  userStore.closeProfileDrawer();
  storeApp.showTransferModal = true;
};
</script>

<template>
  <div class="flex flex-col gap-0.5">
    <div
      class="flex flex-col justify-start items-center gap-3 bg-slate-900 rounded-t-lg py-3 max-w-full"
    >
      <div class="flex justify-start items-center gap-4 bg-slate-900 rounded-lg w-full px-5">
        <img class="rounded-full w-8 md:w-10" src="/images/avatar.png" alt="avatar" />

        <div class="flex flex-col space-between items-start min-w-0 max-w-full">
          <span class="text-white font-semibold text-md text-ellipsis max-w-full overflow-hidden">
            {{ username || "Username" }}
          </span>

          <span
            class="min-w-0 text-slate-400 font-normal text-xs text-ellipsis max-w-full overflow-hidden"
            :class="['AYOR', 'Axnatan85'].includes(username || '') ? 'blur-sm' : ''"
          >
            {{ email }}
          </span>
        </div>
      </div>
      <div class="bg-slate-950 h-0.5 w-full"></div>
      <div class="grid grid-cols-2 gap-2 w-full px-2">
        <div
          class="flex justify-between items-center px-2 bg-slate-950 rounded-lg py-2"
          :class="{
            'col-span-2': userStore.smartWalletAddress,
          }"
        >
          <div class="text-xs text-slate-400">USDC</div>
          <div class="flex gap-2 items-center">
            <div class="text-sm">
              <div class="text-sm">
                <template v-if="userStore.smartWalletAddress">
                  {{ formatNumberLocal(Number(userStore.usdcRemaining)) }}
                </template>
              </div>
            </div>
            <img src="/images/usdc.svg" class="w-4 mb-0.5" alt="POL" />
          </div>
        </div>
        <div
          v-if="!userStore.smartWalletAddress"
          class="flex justify-between items-center px-2 bg-slate-950 rounded-lg py-2"
        >
          <div class="text-xs text-slate-400">POL</div>
          <div class="flex gap-2 items-center">
            <div class="text-sm">{{ formatNumberLocal(Number(web3AuthStore.polBalance)) }}</div>
            <img src="/images/pol.svg" class="w-4 mb-0.5" alt="POL" />
          </div>
        </div>
        <div class="flex justify-between items-center px-2 bg-slate-950 rounded-lg py-2">
          <div class="text-xs text-slate-400">{{ t("_shared.credit", { count: 2 }) }}</div>
          <div class="flex gap-2 items-center">
            <div class="text-sm">
              <div class="text-sm">
                {{ formatNumberLocal(roundToDecimals(userStore.creditBalance, 2)) }}
              </div>
            </div>
            <DollarSign
              :size="16"
              class="mr-0.5 border px-[2px] rounded-full"
              :stroke-width="2"
              alt="Credits"
            />
          </div>
        </div>
        <div class="flex justify-between items-center px-2 bg-slate-950 rounded-lg py-2">
          <div class="text-xs text-slate-400">{{ t("_shared.token", { count: 2 }) }}</div>
          <div class="flex gap-2 items-center">
            <div class="text-sm">
              {{ formatNumberLocal(roundToDecimals(userStore.tokenBalance, 2)) }}
            </div>
            <icon-fg-fungiball-token class="w-5 mb-0.5" />
          </div>
        </div>
      </div>
    </div>
    <div class="grid grid-cols-2 gap-0.5">
      <button
        class="rounded-bl-lg bg-slate-900 hover:bg-slate-800 py-2 px-4 flex-col items-center justify-center gap-1 pt-2.5"
        @click="showDepositModal"
        tabindex="-1"
      >
        <div class="flex items-center w-full justify-center text-slate-300 gap-1">
          <div class="bg-white/10 w-4 h-4 flex items-center justify-center rounded-full">
            <ArrowDown class="h-3" />
          </div>

          <img
            v-if="userStore.migrationToUSDCCompleted"
            src="/images/usdc.svg"
            class="h-4"
            alt="Deposit POL"
          />
          <img v-else src="/images/wpol.svg" class="h-4" alt="Deposit WPOL" />
        </div>
        <span class="text-xs text-slate-400">
          {{ t("_token-transfer.deposit.cta") }}
          <!-- {{ t("drawers.profile.defaultView.wallet.deposit", "Deposit") }} -->
        </span>
      </button>

      <!-- showTransferModal -->
      <button
        class="bg-slate-900 hover:bg-slate-800 py-2 px-4 flex flex-col items-center justify-center gap-1 pt-2.5"
        @click="showTransferModal"
        tabindex="-1"
      >
        <!-- <img src="/images/convert-pol-to-wpol.svg" class="h-[18px]" alt="Convert POL to WPOL" /> -->
        <div class="flex items-center text-slate-300 gap-1">
          <div class="bg-white/10 w-4 h-4 flex items-center justify-center rounded-full">
            <ArrowUp class="h-3" />
          </div>
          <img
            v-if="userStore.migrationToUSDCCompleted"
            src="/images/usdc.svg"
            class="h-4"
            alt="Deposit POL"
          />
          <img v-else src="/images/wpol.svg" class="h-4" alt="Deposit WPOL" />
        </div>
        <span class="text-xs text-slate-400">
          {{ t("_token-transfer.withdraw.cta") }}
          <!-- {{ t("drawers.profile.defaultView.wallet.convert", "Convert") }} -->
        </span>
      </button>
    </div>
  </div>
</template>
